import { Component, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LexiconModule } from '@galaxy/lexicon';
import { ConversationPipesModule } from '../../../../../../../pipes/src/pipes.module';
import {
  ConversationApiService,
  UIButtonInterface,
  UIComponentInterface,
  UIComponentType,
} from '@vendasta/conversation';
import {
  ConversationMessage,
  EscalateToSupportPayload,
} from '../../../../../../../core/src/lib/interface/conversation.interface';
import { ESCALATE_TO_SUPPORT_ACTION_KEY } from '../../../../../../../core/src/lib/conversation.constants';
import { CONVERSATION_CONFIG_TOKEN } from '../../../../../../../core/src/lib/tokens';

import { MatButtonModule } from '@angular/material/button';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export interface ClickedComponentInfo {
  buttonLabel: string;
  buttonId: string;
  clickedAt: Date;
  messageId: string;
}

@Component({
  selector: 'inbox-message-ui-components',
  imports: [CommonModule, LexiconModule, ConversationPipesModule, MatButtonModule],
  templateUrl: './message-ui-components.component.html',
  styleUrl: './message-ui-components.component.scss',
})
export class MessageUIComponentsComponent {
  inputMessage = input.required<ConversationMessage>();
  toolCallStarted = output<void>();
  componentClicked = output<ClickedComponentInfo>();
  private readonly snackbarService = inject(SnackbarService);
  private readonly conversationApiService = inject(ConversationApiService);
  private readonly translationService = inject(TranslateService);
  private readonly conversationConfig = inject(CONVERSATION_CONFIG_TOKEN);

  get messageButtons(): UIComponentInterface[] {
    return (
      this.inputMessage().UIComponents?.filter(
        (component) => component.type === UIComponentType.UI_COMPONENT_TYPE_BUTTON,
      ) ?? []
    );
  }

  async handleButtonClick(button: UIButtonInterface): Promise<void> {
    const clickedComponent = this.inputMessage().UIComponents?.find((component) => component.button?.id === button.id);

    if (clickedComponent && button.label) {
      this.componentClicked.emit({
        buttonLabel: button.label,
        buttonId: button.id || '',
        clickedAt: new Date(),
        messageId: this.inputMessage().messageId || '',
      });
    }

    this.toolCallStarted.emit();

    try {
      await firstValueFrom(
        this.conversationApiService.makeToolCall({
          messageOption: {
            messageId: this.inputMessage().messageId,
            selectedOptionIds: [button.id || ''],
          },
        }),
      );
    } catch (error) {
      console.error('Error making tool call:', error);
      this.snackbarService.openErrorSnack(this.translationService.instant('INBOX.SOMETHING_WENT_WRONG'));
    }

    this.dispatchClientSideAction(button);
  }

  private dispatchClientSideAction(button: UIButtonInterface): void {
    if (button.action === ESCALATE_TO_SUPPORT_ACTION_KEY) {
      this.conversationConfig.openSupportChat?.(parseEscalatePayload(button.payload));
    }
  }
}

function parseEscalatePayload(raw: string | undefined): EscalateToSupportPayload {
  if (!raw) return { conversationSummary: '' };
  try {
    const parsed = JSON.parse(raw) as Partial<EscalateToSupportPayload>;
    return { conversationSummary: parsed.conversationSummary ?? '' };
  } catch {
    return { conversationSummary: '' };
  }
}
